import { Edge, Instance, Node } from "reactflow";
import axios from "axios";
import { webURL, cancelAllRequests } from "./API";
import { operationType } from "./types";
import { reduxUpdateError } from "../redux/reducers/error";
import { reduxUpdateSettings } from "../redux/reducers/settings";
import { reduxUpdatePrevUser } from "../redux/reducers/user";
import {
   advanceExecutionProgress,
   clearExecutionProgress,
   isExecutionErrorOutput,
   markRunEntryNodesExecuting,
} from "./executionProgress";
import { pushBrowserUsePreview } from "../components/run/browserPreview";
import { pushComputerUsePreview } from "../components/run/computerPreview";

// Queue System
const queueNodes: any = [];
let isProcessingQueue = false;

function processQueue(setNodes: any) {
   if (isProcessingQueue || queueNodes.length === 0) return;

   isProcessingQueue = true;
   const updateFunction = queueNodes.shift();
   if (updateFunction) setNodes((nodes: any) => updateFunction(nodes));

   setTimeout(() => {
      isProcessingQueue = false;
      processQueue(setNodes);
   }, 100);
}
export const addUpdateNodeToQueue = (setNodes: any, updateFunction: (nodes: Node<operationType>[]) => void) => {
   queueNodes.push(updateFunction);
   processQueue(setNodes);
}

function stringifyRunValue(value: any): any {
   if (['string', 'number', 'boolean'].includes(typeof value)) return value;
   if (value == null) return '';
   if (typeof value === 'object') return JSON.stringify(value, null, 2);
   return String(value);
}

/** Map a streamed node payload onto a connected Output block without dropping handle misses. */
export function formatConnectedOutputValue(final: any, sourceHandle?: string | null) {
   if (['string', 'number', 'boolean'].includes(typeof final)) return final;
   if (final == null) return '';
   if (Array.isArray(final)) return JSON.stringify(final, null, 2);
   if (typeof final !== 'object') return String(final);

   const handle = String(sourceHandle || '');
   if (handle && final[handle] !== undefined && final[handle] !== null) {
      return stringifyRunValue(final[handle]);
   }

   return JSON.stringify(final, null, 2);
}

// Short Update
export function updateNodeValue(id: string, setNodes: (payload: Node<any>[] | ((nodes: Node<any>[]) => Node<any>[])) => void, value: any) {
   setNodes((nodes) => nodes.map((node) => {
      if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, ...value }}};
      else return node;
   }));
}

// Save all workflow changes
export async function handleWorkflowSave(projectId: string, userToken: string, nodes: Node<any>[], edges: Edge<any>[]) {
   nodes = nodes.map(node => {
      if (!node?.data) return node;
      if (['fileUploader', 'docSearch'].includes(node.data.action) && node.data.value) {
         return { ...node, data: { ...node.data, value: { ...node.data.value, current: [] }}};
      } else if (node.data.value?.isLoading) {
         return { ...node, data: { ...node.data, value: { ...node.data.value, isLoading: false }}};
      }else return node;
   });
   await axios.put(`${webURL}/projects`, { isWorkflow: true, id: projectId, nodes, edges }, { headers: { 'x-auth-token': userToken }});
}

// ----
export function getvaluesUsingVariables(data: { [key: string]: any }, nodes: Node<operationType>[]): { [key: string]: string } {
   const workflow: { [key: string]: any } = {};
   for (const node of nodes) workflow[node.data.index as string ] = node.data.value || '';

   for (const key in data) {
      if (data[key]) data[key] = data[key].toString().replace(/{(.*?)}/g, (_: any, key: any) => {
         const keys = key.split('.');
         let value = workflow;
 
         for (const k of keys) {
            if (value && typeof value === 'object' && k in value) value = value[k];
            else return `{${key}}`;
         }
         return typeof value === 'object' ? JSON.stringify(value) : String(value);
     });
   }
   return data;
}

// ---
export const validJSON = (data: any) => { 
   try { 
      const json = JSON.parse(data); 
      return json; 
   } catch { 
      return data; 
   } 
};

// ---
export const handleRemoveVariablesFromBlock = (
   { id, getNodes, getEdges, setNodes, setEdges }: 
   { id: string, getNodes: () => Node<any>[], getEdges: () => Edge<any>[], setNodes: Instance.SetNodes<any>, setEdges: Instance.SetEdges<any> }
) => {
   const nodes = getNodes();
   const edges = getEdges().filter(edge => edge.target === id || edge.source === id);

   for (const edge of edges) {
      const target = nodes.find(node => node.id === edge?.target);
      const source = nodes.find(node => node.id === edge?.source);

      addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node) => {
         if (node.id === target?.id && !node.data.isMediator) {
            const variables = { ...node.data.value?.variables };
            for (let key in variables?.input) variables.input[key] = variables.input[key].filter((inx: string) => inx !== source?.data.index);
            return { ...node, data: { ...node.data, value: { ...node.data.value, variables }}};

         } else if (node.id === source?.id && !node.data.isMediator) {
            const variables = { ...node.data.value?.variables };
            for (let key in variables?.output) variables.output[key] = variables.output[key].filter((inx: string) => inx !== target?.data.index);
            return { ...node, data: { ...node.data, value: { ...node.data.value, variables }}};
         }
         else return node;
      }));
   }
   addUpdateNodeToQueue(setNodes, (nodes) => nodes.filter((node) => node.id !== id));
   setEdges((edges) => edges.filter((edge) => edge.target !== id && edge.source !== id));
}

// Delete a node entirely, including any AI Agent chat threads tied to it.
export async function handleRemoveNode(
   { id, action, index, projectId, userId, getNodes, getEdges, setNodes, setEdges }:
   {
      id: string, action?: string, index?: string, projectId?: string | null, userId?: string,
      getNodes: () => Node<any>[], getEdges: () => Edge<any>[], setNodes: Instance.SetNodes<any>, setEdges: Instance.SetEdges<any>
   }
) {
   handleRemoveVariablesFromBlock({ id, getNodes, getEdges, setNodes, setEdges });

   if (action === 'aiAgent' && projectId && index && userId) {
      await axios.delete(`${webURL}/agents/aiAgentChat?thread_id=${encodeURIComponent(`${projectId}-${index}-user-${userId}`)}`).catch(() => {});
      await axios.delete(`${webURL}/agents/aiAgentChat?thread_id=${encodeURIComponent(`${projectId}-${index}-admin-${userId}`)}`).catch(() => {});
   }
}

// Run workflow: missing handleValuesFeatures({ isRun: true/false }) solution: maybe raise to redux; 
export async function handleWorkflowRun(
   projectId: string, userToken: string, nodes: Node<any>[], edges: Edge<any>[], isRunRealTime: boolean,
   isRun: boolean, apiKey: string, dispatch: any, navigate: any, setNodes: any, handleValuesFeatures?: (data: any) => void,
   targetIndex?: string
) {
   const triggerProcessRefresh = () => {
      dispatch(reduxUpdateSettings({ isRunForProcess: true }));
      setTimeout(() => {
         dispatch(reduxUpdateSettings({ isRunForProcess: false }));
      }, 60);
   };
   if (isRunRealTime) return;
   const nodesFirst = nodes;
   const nodesSave = [...nodesFirst];
   try {
      if (isRun) {
         cancelAllRequests();
         if (nodesFirst.some(node => node.data.action === 'flowQueue')) navigate(0);
      } else {
         dispatch(reduxUpdateError(''));
         dispatch(reduxUpdateSettings({ isWorkflowRun: true, showContactUS: false }));
         if (handleValuesFeatures) handleValuesFeatures({ isRun: true });
         addUpdateNodeToQueue(setNodes, (nodes) =>
            markRunEntryNodesExecuting(
               clearExecutionProgress(nodes, { clearOutput: true }),
               edges,
               targetIndex
            )
         );
         // start
         for (const item of nodesFirst) {
            if (item.data.executeRun) await item.data.executeRun({ projectId, id: item.id, nodes: nodesFirst, setNodes, edges });
         }
         await handleWorkflowSave(projectId as string, userToken as string, nodesFirst, edges);

         addUpdateNodeToQueue(setNodes, (nodes) =>
            markRunEntryNodesExecuting(
               clearExecutionProgress(nodes, { clearOutput: true }),
               edges,
               targetIndex
            )
         );

         // request
         const runURL = `${webURL}/run/${projectId}?withStream=true${targetIndex ? `&targetIndex=${encodeURIComponent(targetIndex)}` : ''}`;
         if (nodesFirst.some((node) => node.data?.action === 'browserUse')) {
            pushBrowserUsePreview({ projectId, waiting: true, operation: 'starting', done: false });
         }
         if (nodesFirst.some((node) => node.data?.action === 'computerUse')) {
            pushComputerUsePreview({
               projectId,
               waiting: true,
               operation: 'starting',
               done: false,
               reason: 'Creating isolated Linux desktop. This can take 15–40 seconds.',
            });
         }
         const response = await fetch(runURL, {
            method: 'POST', headers: { 'Authorization': `Bearer ${apiKey}` }
         });
         const reader = response.body?.getReader();
         const decoder = new TextDecoder('utf-8');

         // build response
         if (reader) {
            let bufferedText = '';

            while (true) {
               const { done, value } = await reader.read();
               const chunk = decoder.decode(value || new Uint8Array());

               if (chunk) {
                  bufferedText += chunk;
                  const parts = bufferedText.split('\n');
                  bufferedText = parts.pop()!;

                  for (const part of parts) {
                     if (!part.trim()) continue;
                     try {
                        const response = JSON.parse(part);
                        if (response.browserUsePreview) {
                           pushBrowserUsePreview(response.browserUsePreview);
                           continue;
                        }
                        if (response.computerUsePreview) {
                           pushComputerUsePreview(response.computerUsePreview);
                           continue;
                        }
                        const index = Object.keys(response)[0];
                        const item = nodes.find(node => node.data.index === index);
                        const sources: operationType[] = [];

                        for (const edge of edges) {
                           if (edge.source === item?.id) {
                              for (const node of nodes) {
                                 if (node.id === edge.target && node.data.action === 'output') {
                                    sources.push({ id: node.id, sourceHandle: edge.sourceHandle, ...node.data });
                                 }
                              }
                           }
                        }
                        addUpdateNodeToQueue(setNodes, (nodes) => (nodes).map((node) => {
                           const find = sources.find(source => source.id === node.id && node.data.action === 'output');
                           const findFromEnd = response.nodes?.find((n: any) => n.id === node.id);

                           if (find) {
                              const final = response[index]?.Output || response[index];
                              if (final?.error) dispatch(reduxUpdateSettings({ showContactUS: true }));
                              return {
                                 ...node, data: {
                                    ...node.data,
                                    value: formatConnectedOutputValue(final, find.sourceHandle)
                                 }
                              }
                           } else if (findFromEnd && node.data.outputType) {
                              return { ...node, data: { ...node.data, value: { ...findFromEnd.data.value, isTemporaryMemory: false } } };
                           } else if (node.data.index === index && node.data.action === 'output') {
                              const incomingHandle = edges.find(edge => edge.target === node.id)?.sourceHandle;
                              return {
                                 ...node,
                                 data: {
                                    ...node.data,
                                    value: formatConnectedOutputValue(response[index]?.Output || response[index], incomingHandle || 'Output')
                                 }
                              };
                           } else if (node.data.index === index) {
                              return { ...node, data: { ...node.data, value: { ...node.data.value, ...(response[index]?.updateNodeValue || {}), isTemporaryMemory: false } } };
                           } else if (node.data.outputType) {
                              return { ...node, data: { ...node.data, value: { ...node.data.value, isTemporaryMemory: false } } };
                           } else return node;
                        }));
                        if (item?.id) {
                           addUpdateNodeToQueue(setNodes, (nodes) =>
                              advanceExecutionProgress(nodes, edges, item.id, index, response[index])
                           );
                        } else {
                           addUpdateNodeToQueue(setNodes, (nodes) => (nodes).map((node) => {
                              if (node.data.index === index) {
                                 const output = response[index];
                                 return {
                                    ...node,
                                    data: {
                                       ...node.data,
                                       output,
                                       isExecuting: false,
                                       executionError: isExecutionErrorOutput(output),
                                    },
                                 };
                              } else return node;
                           }));
                        }
                     } catch (e) {
                        console.error('Error parsing chunk:', part);
                        console.error(e);
                     }
                  }
               }
               if (done) break;
            }
            // axios.post(`${webURL}/process/monitor`, { project: projectId, type: 'success' }).then(_=> {
            //    dispatch(reduxUpdateSettings({ isRunForProcess: true }));
            //    setTimeout(() => { dispatch(reduxUpdateSettings({ isRunForProcess: false })) }, 0);
            // });
            const plan = (await axios.get(`${webURL}/process/info?type=plan`)).data;
            dispatch(reduxUpdatePrevUser({ plan }));
         }
         // dispatch(reduxUpdateSettings({ isWorkflowRun: false }));
         handleWorkflowSave(projectId as string, userToken as string, nodes, edges)
      }
   } catch (err) { // reset
      console.error(err);
      // dispatch(reduxUpdateSettings({ isWorkflowRun: false }));
      setNodes(nodesSave.map((node: any) => {
         if (node.data.outputType) return { ...node, data: { ...node.data, value: { ...node.data.value, isTemporaryMemory: false } } };
         else return node;
      }));
      // dispatch(reduxUpdateSettings({ isRunForProcess: true }));
      // setTimeout(() => { dispatch(reduxUpdateSettings({ isRunForProcess: false })) }, 0);
      // @ts-ignore
      const message = err?.response?.data || err?.message || err?.toString?.() || 'An error occurred';
      // axios.post(`${webURL}/process/monitor`, { project: projectId, type: 'failure', description: JSON.stringify(message, null, 1) });
      dispatch(reduxUpdateError(message));
   } finally {
      dispatch(reduxUpdateSettings({ isWorkflowRun: false }));
      // Stop hop animation; keep outputs / real executionError for node badges.
      addUpdateNodeToQueue(setNodes, (nodes) =>
         nodes.map((node) => ({
            ...node,
            data: { ...node.data, isExecuting: false },
         }))
      );
      triggerProcessRefresh();
      if (handleValuesFeatures) handleValuesFeatures({ isRun: false });
   }
}